import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Bell } from "lucide-react";

interface NotificationSettings {
  emailNotifications: boolean;
  pushNotifications: boolean;
  notifyOnComments: boolean;
  notifyOnLikes: boolean;
  notifyOnSaves: boolean;
}

interface NotificationSectionProps {
  settings: NotificationSettings;
  onSettingChange: (setting: string, value: boolean) => void;
}

export const NotificationSection = ({ settings, onSettingChange }: NotificationSectionProps) => {
  return (
    <Card className="border-border/50 bg-card/50 backdrop-blur-sm">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Bell className="h-5 w-5 text-primary" />
          Notifications
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Master Toggles */}
        <div className="space-y-4 pb-4 border-b border-border/50">
          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label className="text-base">Email Notifications</Label>
              <p className="text-sm text-muted-foreground">
                Receive notifications via email
              </p>
            </div>
            <Switch
              checked={settings.emailNotifications}
              onCheckedChange={(checked) => onSettingChange('emailNotifications', checked)}
            />
          </div>
          
          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label className="text-base">In-App Notifications</Label>
              <p className="text-sm text-muted-foreground">
                Show notifications within the app
              </p>
            </div>
            <Switch
              checked={settings.pushNotifications}
              onCheckedChange={(checked) => onSettingChange('pushNotifications', checked)}
            />
          </div>
        </div>

        {/* Notification Types */}
        <div className="space-y-4">
          <Label className="text-sm text-muted-foreground uppercase tracking-wide">
            Notify me about
          </Label>
          
          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label className="text-base">Comments</Label>
              <p className="text-sm text-muted-foreground">
                When someone comments on your prompts
              </p>
            </div>
            <Switch
              checked={settings.notifyOnComments}
              onCheckedChange={(checked) => onSettingChange('notifyOnComments', checked)}
            />
          </div>

          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label className="text-base">Likes</Label>
              <p className="text-sm text-muted-foreground">
                When someone likes your prompts
              </p>
            </div>
            <Switch
              checked={settings.notifyOnLikes}
              onCheckedChange={(checked) => onSettingChange('notifyOnLikes', checked)}
            />
          </div>

          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label className="text-base">Saves</Label>
              <p className="text-sm text-muted-foreground">
                When someone saves your prompts
              </p>
            </div>
            <Switch
              checked={settings.notifyOnSaves}
              onCheckedChange={(checked) => onSettingChange('notifyOnSaves', checked)}
            />
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
